import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { BeginTotpSetupDto, MfaActions, MfaSettingsDto } from "@ha/feature/mfa";
import { Actions, ofActionSuccessful, Select, Store } from "@ngxs/store";
import { distinctUntilChanged, Observable } from "rxjs";
import { MfaState } from "../../states/mfa.state";

@Component({
    selector: "app-mfa-setup",
    templateUrl: "./mfa-setup.component.html",
    styleUrls: ["./mfa-setup.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MfaSetupComponent implements OnInit {
    @Select(MfaState.settings)
    public settings$: Observable<MfaSettingsDto | null>;

    @Select(MfaState.setup)
    public setup$: Observable<BeginTotpSetupDto | null>;

    @Input()
    public cancelRoute?: string[];

    @Input()
    public successRoute?: string[];

    private cancelRouteValue: string[] = ["/user"];
    private successRouteValue: string[] = ["/user"];

    public step: "code" | "verify" = "code";

    public loading = true;
    public copying = false;
    public error = "";
    public qrLoading = true;
    public qrError = false;

    public setup?: BeginTotpSetupDto;

    public form: FormGroup;

    public readonly ConfirmSetupAction = MfaActions.ConfirmSetup;

    public constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly fb: FormBuilder,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly cdr: ChangeDetectorRef,
    ) {
    }

    public ngOnInit(): void {
        // Read route data for cancel and success routes, or use Input, or default
        const routeData = this.route.snapshot.data;
        this.cancelRouteValue = this.cancelRoute || routeData?.["cancelRoute"] || ["/user"];
        this.successRouteValue = this.successRoute || routeData?.["successRoute"] || ["/user"];

        this.loading = true;
        this.qrLoading = true;

        this.form = this.fb.group({
            verificationCode: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]],
        });

        // Dispatch and subscribe to GetSettings action result
        this.store.dispatch(new MfaActions.GetSettings()).subscribe({
            next: () => {
                this.loading = false;
                this.cdr.detectChanges();

                // Check if MFA is already enabled and redirect
                const settings = this.store.selectSnapshot(MfaState.settings);
                if (settings?.isEnabled) {
                    this.router.navigate(this.successRouteValue);
                }
            },
            error: (err) => {
                console.error(err);
                this.error = "setupError";
                this.loading = false;
                this.qrLoading = false;
                this.qrError = true;
                this.cdr.detectChanges();
            },
        });

        // Dispatch and subscribe to BeginSetup action result
        this.store.dispatch(new MfaActions.BeginSetup()).subscribe({
            next: () => {
                const setup = this.store.selectSnapshot(MfaState.setup);
                if (setup) {
                    this.setup = setup;
                    this.qrLoading = false;
                    this.qrError = false;
                    this.cdr.detectChanges();
                }
            },
            error: () => {
                this.qrError = true;
                this.qrLoading = false;
                this.cdr.detectChanges();
            },
        });

        this.form.controls.verificationCode.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
            if (this.error) {
                this.error = "";
                this.cdr.markForCheck();
            }
        });

        // Listen for successful ConfirmSetup action completion
        this.actions$.pipe(ofActionSuccessful(MfaActions.ConfirmSetup)).subscribe(() => {
            this.store.dispatch(new MfaActions.GetSettings());
            this.router.navigate(this.successRouteValue, { queryParams: { mfaEnabled: 1 } });
        });
    }

    public get qrDataUri(): string | null {
        const b64 = this.setup?.qrCodePngBase64;
        return b64 ? `data:image/png;base64,${b64}` : null;
    }

    public onQrImageLoad(): void {
        this.qrLoading = false;
        this.qrError = false;
        this.cdr.detectChanges();
    }

    public onQrImageError(): void {
        this.qrLoading = false;
        this.qrError = true;
        this.cdr.detectChanges();
    }

    public beginSetup(): void {
        this.error = "";
        this.qrLoading = true;
        this.qrError = false;

        this.store.dispatch(new MfaActions.BeginSetup()).subscribe({
            next: () => {
                const setup = this.store.selectSnapshot(MfaState.setup);
                if (setup) {
                    this.setup = setup;
                    this.qrLoading = false;
                    this.qrError = false;
                    this.cdr.detectChanges();
                }
            },
            error: () => {
                this.qrError = true;
                this.qrLoading = false;
                this.cdr.detectChanges();
            },
        });
    }

    public async copyKey(): Promise<void> {
        if (!this.setup?.sharedKey) {
            return;
        }
        try {
            this.copying = true;
            await navigator.clipboard.writeText(this.setup.sharedKey);
            this.copying = false;
            this.cdr.markForCheck();
        } catch {
            this.copying = false;
            this.cdr.markForCheck();
        }
    }

    public verify(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.error = "";
        const code = this.form.value.verificationCode!;

        this.store.dispatch(new MfaActions.ConfirmSetup(code)).subscribe({
            error: (err) => {
                const errorCode = err?.errorCode || err?.error?.errorCode;
                this.error = errorCode ? `errors.${errorCode}` : "errors.UNKNOWN";
                this.cdr.detectChanges();
            },
        });
    }

    public cancel(): void {
        this.router.navigate(this.cancelRouteValue);
    }
}
